import React, { createContext, useContext, useEffect, useState } from "react";

import { usePhotoUploadsInDropzone } from "../../PhotoUploadCore/hooks";

export const SelectCheckboxContext = createContext();

export const useSelect = () => {
    const [lastClickedIndex, updateLastClickedIndex] = useState(null);

    const {
        isChecked,
        isSelectAllChecked,
        toggleChecked,
        clearAllChecked,
        checkAll,
        quantityChecked,
        checkItems,
        unCheckItems,
        uploadIds
    } = useContext(SelectCheckboxContext);

    const handleItemContentChange = (e, gridIndex, fileId) => {
        e.persist();
        //keyboard shift + mouse click to select range:
        if (
            e.nativeEvent.shiftKey &&
            lastClickedIndex !== null &&
            quantityChecked > 0
        ) {
            const startIndex = lastClickedIndex;
            const endIndex = gridIndex;
            const minIndex = Math.min(startIndex, endIndex);
            const maxIndex = Math.max(startIndex, endIndex);

            // create a range with the first item checked & last item checked & all missing ranges in the middle
            const range = uploadIds.slice(minIndex, maxIndex + 1);

            // determine whether range is going to become checked or unchecked
            const fileIsChecked = isChecked[fileId] === true;
            const nextValue = !fileIsChecked;

            if (nextValue) {
                checkItems(range);
            } else {
                unCheckItems(range);
            }
        }

        updateLastClickedIndex(gridIndex);
    };

    const handleSelectAllChange = () => {
        if (isSelectAllChecked) {
            clearAllChecked();
            updateLastClickedIndex(null);
        } else {
            checkAll();
        }
    };

    return {
        isChecked,
        handleItemContentChange,
        handleSelectAllChange,
        isSelectAllChecked,
        toggleChecked,
        clearAllChecked,
        checkAll,
        quantityChecked,
        checkItems
    };
};

export const SelectProvider = ({ children, uploadIds = [] }) => {
    const [isChecked, setChecked] = useState({}); // please refrain from using `setChecked` directly; try one of the API functions below!

    // Toggle group of ids to opposite previous value
    const toggleChecked = (idsToToggle = []) => {
        if (idsToToggle.length === 1) {
            setChecked(prevState => {
                return {
                    ...prevState,
                    [idsToToggle[0]]: prevState[idsToToggle[0]]
                        ? !prevState[idsToToggle[0]]
                        : true
                };
            });
        } else {
            idsToToggle.forEach(id =>
                setChecked(prevState => {
                    return {
                        ...prevState,
                        [id]: !prevState[id]
                    };
                })
            );
        }
    };

    // Set ALL ids to false
    const clearAllChecked = () => {
        const ids = Object.keys(isChecked);
        unCheckItems(ids);
    };

    // Set ALL ids to true
    const checkAll = () => {
        checkItems(uploadIds);
    };

    // Set specific ids to true
    const checkItems = (idsToCheck = []) => {
        idsToCheck.forEach(id =>
            setChecked(prevState => {
                return {
                    ...prevState,
                    [id]: true
                };
            })
        );
    };

    // Set specific ids to false
    const unCheckItems = (idsToUncheck = []) => {
        idsToUncheck.forEach(id =>
            setChecked(prevState => {
                return {
                    ...prevState,
                    [id]: false
                };
            })
        );
    };

    const [isSelectAllChecked, setSelectAllChecked] = useState(false);
    const [quantityChecked, setQuantitySelected] = useState(0);
    useEffect(() => {
        if (!!uploadIds.length) {
            const checkedIds = Object.keys(isChecked).filter(
                id => !!isChecked[id]
            );
            if (checkedIds.length === uploadIds.length) {
                setSelectAllChecked(true);
            } else {
                setSelectAllChecked(false);
            }
        }

        const checkedIds = Object.keys(isChecked).filter(id => !!isChecked[id]);
        setQuantitySelected(checkedIds.length);
    }, [uploadIds.length, isChecked]);

    return (
        <SelectCheckboxContext.Provider
            value={{
                isChecked,
                toggleChecked,
                isSelectAllChecked,
                clearAllChecked,
                checkAll,
                quantityChecked,
                checkItems,
                unCheckItems,
                uploadIds
            }}
        >
            {children}
        </SelectCheckboxContext.Provider>
    );
};

export const SelectProviderForDropzone = ({ children }) => {
    const { uploadIds } = usePhotoUploadsInDropzone();

    return <SelectProvider uploadIds={uploadIds}>{children}</SelectProvider>;
};
